/**
 * StudyPicker — shared link-or-create study selector (source-of-truth plan §3.6).
 *
 * Search-as-you-type against the platform registry (`GET /api/registry/studies`,
 * scoped to a site or network), select an existing study, or follow the
 * "Create new study in eReg" deep link carrying the typed text + return_to.
 * This is the front half of every module's "create study" button from P2 on.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Loader2, FlaskConical, Check } from 'lucide-react';
import api from '../services/api';
import SearchInput from './SearchInput';
import { eregStudyCreateLink } from './returnTo';

export interface RegistryStudy {
  id: string;
  site_id: string;
  protocol_number: string;
  study_nickname?: string | null;
  sponsor_name?: string | null;
  therapeutic_area?: string | null;
  phase?: string | null;
  status?: string | null;
  site?: { id: string; name: string } | null;
}

interface StudyPickerProps {
  /** Scope the registry search to one site… */
  siteId?: string;
  /** …or a whole network (CRM). Omit both to search all accessible sites. */
  networkId?: string;
  onSelect: (study: RegistryStudy) => void;
  selectedId?: string | null;
  /** Where eReg should return after "Create new study" (relative path). */
  createReturnTo?: string;
  createReturnLabel?: string;
  /** Hide the create deep link (pure link-only pickers). */
  allowCreate?: boolean;
  /**
   * Search endpoint (default the platform registry API). CRM passes its own
   * proxy ('/api/crm/registry/studies') — its tenant rule spans the whole
   * network regardless of the caller's per-site grants (plan §3.4).
   */
  searchPath?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export default function StudyPicker({
  siteId,
  networkId,
  onSelect,
  selectedId = null,
  createReturnTo,
  createReturnLabel,
  allowCreate = true,
  searchPath = '/api/registry/studies',
  placeholder = 'Search studies by protocol number, name, or sponsor…',
  className = '',
  autoFocus = false,
}: StudyPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RegistryStudy[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (query.trim()) params.set('q', query.trim());
        if (siteId) params.set('site_id', siteId);
        if (networkId) params.set('network_id', networkId);
        const res = await api.get(`${searchPath}?${params.toString()}`);
        if (seq !== requestSeq.current) return; // stale response
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setResults([]);
        setError('Could not search the study registry');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, siteId, networkId, searchPath]);

  // Close on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const createLink = useMemo(
    () =>
      eregStudyCreateLink({
        protocolNumber: query.trim() || undefined,
        returnTo: createReturnTo,
        returnLabel: createReturnLabel,
      }),
    [query, createReturnTo, createReturnLabel]
  );

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <SearchInput
        value={query}
        onChange={(v) => { setQuery(v); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        label="Search studies"
        autoFocus={autoFocus}
      />

      {open && (
        <div className="absolute z-40 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <ul className="max-h-72 overflow-y-auto py-1" role="listbox" aria-label="Study results">
            {loading && (
              <li className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
                Searching…
              </li>
            )}
            {!loading && error && (
              <li className="px-3 py-2 text-sm text-red-600 dark:text-red-400">{error}</li>
            )}
            {!loading && !error && results.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No studies found{query.trim() ? ` for “${query.trim()}”` : ''}
              </li>
            )}
            {!loading && results.map((study) => (
              <li key={study.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={study.id === selectedId}
                  onClick={() => { onSelect(study); setOpen(false); }}
                  className={`flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors duration-150 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-700/50 dark:focus:bg-gray-700/50 ${
                    study.id === selectedId ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <FlaskConical className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                      <span className="truncate">{study.protocol_number}</span>
                      {study.id === selectedId && <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" />}
                    </span>
                    <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                      {[study.study_nickname, study.sponsor_name, study.site?.name].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {allowCreate && (
            <Link
              to={createLink}
              className="flex items-center gap-1.5 border-t border-gray-100 px-3 py-2 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:border-gray-700 dark:text-primary-400 dark:hover:bg-primary-900/20"
            >
              <Plus className="h-4 w-4" />
              Create new study in eReg{query.trim() ? ` — “${query.trim()}”` : ''} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
